import { useState, useEffect } from 'react'
import { Clock, AlertTriangle } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface WhatsAppWindowIndicatorProps {
  lastInboundAt: string | null
  className?: string
}

// Calculate if within 24h window and time remaining
function calculateWindow(lastInboundAt: string | null): {
  isWithinWindow: boolean
  expiresAt: Date | null
  hoursRemaining: number
  minutesRemaining: number
  timeRemainingFormatted: string
} {
  if (!lastInboundAt) {
    return {
      isWithinWindow: false,
      expiresAt: null,
      hoursRemaining: 0,
      minutesRemaining: 0,
      timeRemainingFormatted: 'Expirada',
    }
  }

  const lastInbound = new Date(lastInboundAt)
  const now = new Date()
  const windowDurationMs = 24 * 60 * 60 * 1000 // 24 hours in ms
  const expiresAt = new Date(lastInbound.getTime() + windowDurationMs)
  const timeRemainingMs = expiresAt.getTime() - now.getTime()

  if (timeRemainingMs <= 0) {
    return {
      isWithinWindow: false,
      expiresAt,
      hoursRemaining: 0,
      minutesRemaining: 0,
      timeRemainingFormatted: 'Expirada',
    }
  }

  const hoursRemaining = Math.floor(timeRemainingMs / (1000 * 60 * 60))
  const minutesRemaining = Math.floor((timeRemainingMs % (1000 * 60 * 60)) / (1000 * 60))

  let timeRemainingFormatted: string
  if (hoursRemaining > 0) {
    timeRemainingFormatted = `${hoursRemaining}h ${minutesRemaining}m`
  } else {
    timeRemainingFormatted = `${minutesRemaining}m`
  }

  return {
    isWithinWindow: true,
    expiresAt,
    hoursRemaining,
    minutesRemaining,
    timeRemainingFormatted,
  }
}

export default function WhatsAppWindowIndicator({
  lastInboundAt,
  className = '',
}: WhatsAppWindowIndicatorProps) {
  const [windowState, setWindowState] = useState(() => calculateWindow(lastInboundAt))

  // Update every minute
  useEffect(() => {
    setWindowState(calculateWindow(lastInboundAt))

    const interval = setInterval(() => {
      setWindowState(calculateWindow(lastInboundAt))
    }, 60000) // Update every minute

    return () => clearInterval(interval)
  }, [lastInboundAt])

  const { isWithinWindow, hoursRemaining, timeRemainingFormatted } = windowState

  // Determine urgency level for styling
  const isUrgent = isWithinWindow && hoursRemaining < 2
  const isWarning = isWithinWindow && hoursRemaining < 6 && !isUrgent

  if (!isWithinWindow) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 ${className}`}>
            <AlertTriangle className="w-3 h-3" />
            <span>Janela expirada</span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <p>A janela de 24 horas expirou. Use um template pré-aprovado para retomar a conversa.</p>
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
            isUrgent
              ? 'bg-orange-100 text-orange-700'
              : isWarning
              ? 'bg-yellow-100 text-yellow-700'
              : 'bg-green-100 text-green-700'
          } ${className}`}
        >
          <Clock className="w-3 h-3" />
          <span>{timeRemainingFormatted}</span>
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs">
        <p>Tempo restante na janela de 24 horas do WhatsApp. Após expirar, só poderá enviar templates pré-aprovados.</p>
      </TooltipContent>
    </Tooltip>
  )
}

// Export utility function for use in other components
export { calculateWindow }
